import React, { FC } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { createMarkup, useResponsiveType } from '../../utils';

// Interface

export interface HtmlProps {
  children?: any;
  small?: boolean; // For blocks of small print text
  id?: string;
}

interface StyledHtmlProps {
  small?: boolean;
}

// Styles

export const StyledHtml = styled.div<StyledHtmlProps>`
  ${({ theme: { space, colors }, small }) => css`
    > * + * {
      margin-block-start: ${space.medium};
    }

    ${small &&
    css`
      > * + * {
        margin-block-start: ${space.xSmall};
      }
    `}

    ol,
    ul {
      li + li {
        margin-top: ${space.medium};
      }
    }

    p strong {
      ${useResponsiveType('bodyBold')}
    }

    // Links

    a {
      color: ${colors.darkBlue};
      text-decoration: underline;

      &:hover {
        // TODO ??
      }
    }

    // List styles

    ul {
      list-style-type: none;
      padding-left: 0;

      li {
        position: relative;
        padding-left: calc(${space.xxxSmall} + ${space.xxSmall});

        &:before {
          content: '';
          position: absolute;
          top: ${space.xxxSmall};
          border-radius: 50%;
          left: 0;
          width: ${space.xxxSmall};
          height: ${space.xxxSmall};
          background-color: ${colors.darkBlue};
        }
      }
    }

    ol {
      list-style: none;
      counter-reset: ol-counter;

      li {
        counter-increment: ol-counter;
        position: relative;
        padding-left: calc(${space.small} + ${space.xxSmall});

        &:before {
          content: counter(ol-counter);
          color: ${colors.white};
          background-color: ${colors.darkBlue};
          border-radius: 50%;
          width: ${space.small};
          height: ${space.small};
          display: grid;
          place-items: center;
          ${useResponsiveType('bodyLargeBold')}
          position: absolute;
          top: 0;
          left: 0;
        }
      }
    }

    // Typography

    p.large {
      ${useResponsiveType('bodyLarge')}

      strong {
        ${useResponsiveType('bodyLargeBold')}
      }
    }

    p.small {
      ${useResponsiveType('bodySmall')}

      strong {
        ${useResponsiveType('bodySmallBold')}
      }
    }

    h3.secondary {
      ${useResponsiveType('secondaryHeadingMedium')}

      strong {
        ${useResponsiveType('secondaryHeadingMediumBold')}
      }
    }

    h3.secondary-small {
      ${useResponsiveType('secondaryHeadingSmall')}

      strong {
        ${useResponsiveType('secondaryHeadingSmallBold')}
      }
    }

    h1,
    .large {
      ${useResponsiveType('headingLarge')}
    }

    h2 {
      ${useResponsiveType('headingMedium')}
    }

    h3 {
      ${useResponsiveType('headingSmall')}
    }

    h4,
    h5,
    h6 {
      ${useResponsiveType('headingXSmall')}
    }

    strong {
      ${useResponsiveType('bodyBold')}
    }
  `};
`;

// JSX

export const Html: FC<React.PropsWithChildren<HtmlProps>> = ({
  children,
  small,
  id,
  ...rest
}) => (
  <StyledHtml id={id} {...rest} small={small}>
    {typeof children === 'string' ? createMarkup(children) : children}
  </StyledHtml>
);

export default Html;
